import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useFindFirst, useGlobalAction } from '@gadgetinc/react';
import { FiSearch, FiExternalLink, FiChevronDown, FiChevronUp, FiDownload, FiCopy } from 'react-icons/fi';
import { api } from '../api';
import ResumeConversationModal from '../components/ResumeConversationModal';
import { devLog, devError, devLogData } from '../lib/devUtils';
import { useUserWithRoles } from '../hooks/useUserWithRoles';

const buildActionErrorContent = (payload, fallbackMessage) => {
  const publicError = payload?.error || payload?.result?.error || fallbackMessage;
  const technicalError = payload?.technical_error || payload?.result?.technical_error;
  const stage = payload?.stage || payload?.result?.stage;
  const lines = [publicError].filter(Boolean);

  if (technicalError && technicalError !== publicError) {
    lines.push(`Details: ${technicalError}`);
  }

  if (stage) {
    lines.push(`Stage: ${stage}`);
  }

  return lines.join('\n');
};

const AgentChat = () => {
  const { agentName } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const processedActionData = useRef(null);
  const processedResumeData = useRef(null);
  const activeBackgroundJobsRef = useRef(new Set());
  const isMountedRef = useRef(true);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(true);
  const [localSessionId, setLocalSessionId] = useState(null);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isLoadingInitialMessage, setIsLoadingInitialMessage] = useState(false);
  const [showStudyBar, setShowStudyBar] = useState(false);
  const [hasUsedStudyMode, setHasUsedStudyMode] = useState(false);
  const [expandedSearches, setExpandedSearches] = useState({});
  const [citationExportFormat, setCitationExportFormat] = useState('APA');

  const focusInput = () => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  // Get user role information
  const currentUser = useUserWithRoles();
  const normalizedAgentName = String(agentName || '').toLowerCase();
  const isMaiaChat = normalizedAgentName === 'maia';
  const maiaQuickActions = [
    {
      label: 'Quiz Weak Areas',
      prompt: 'Quiz me on the areas I most need to improve. Give me 5 mixed EMS questions with the answer key and concise rationales.'
    },
    {
      label: 'Build Study Plan',
      prompt: 'Build me a focused study plan for this week based on the areas I need the most work in.'
    },
    {
      label: 'Clinical Application',
      prompt: 'Give me 5 clinical application questions that reflect realistic EMS decisions, then explain each answer.'
    },
    {
      label: 'Rapid Review',
      prompt: 'Give me a rapid review of the most important concepts I should know right now, with mnemonics if helpful.'
    }
  ];

  const resizeInput = (element = inputRef.current) => {
    if (!element || typeof window === "undefined") return;

    element.style.height = "auto";
    const computedMaxHeight = Number.parseFloat(window.getComputedStyle(element).maxHeight) || 120;
    const nextHeight = Math.min(element.scrollHeight, computedMaxHeight);
    element.style.height = `${nextHeight}px`;
    element.style.overflowY = element.scrollHeight > computedMaxHeight ? "auto" : "hidden";
    element.scrollTop = element.scrollHeight;
  };

  // Find agent by name field (case-sensitive match to URL parameter)
  const [{ data: agentConfig, fetching: loadingAgent, error: agentError }] = useFindFirst(
    api.agentConfiguration,
    {
      filter: {
        name: { equals: agentName },
        isActive: { equals: true }
      },
      select: {
        id: true,
        name: true,
        description: true,
        capabilities: true,
        isActive: true,
        initiatorMessage: {
          markdown: true
        }
      }
    }
  );

  // Agent chat message action
  const [{ data: actionData, fetching: sendingMessage, error: sendError }, runAgentChatMessageV2] = useGlobalAction(api.agentChatMessageV2);
  const [{ data: resumeConversationData, fetching: loadingResumedConversation, error: resumeConversationError }, loadConversationHistory] = useGlobalAction(api.getAgentConversationHistory);
  const [, pollMaiaPracticeGeneration] = useGlobalAction(api.pollMaiaPracticeGeneration);
  const effectiveActionData = actionData;
  const effectiveSendingMessage = sendingMessage;
  const effectiveSendError = sendError;
  const runChatAction = runAgentChatMessageV2;

  // Generate localSessionId on component mount
  useEffect(() => {
    const generateSessionId = () => {
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      return `${agentName}_${timestamp}_${randomString}`;
    };
    
    if (!localSessionId) {
      setLocalSessionId(generateSessionId());
    }
  }, [agentName, localSessionId]);

  useEffect(() => {
    setShowStudyBar(isMaiaChat);
    setHasUsedStudyMode(false);
  }, [isMaiaChat]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    resizeInput();
  }, [inputMessage]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isResuming || !localSessionId || !agentConfig) return;

    loadConversationHistory({
      localSessionId,
      agentType: agentName,
      includeMessages: true,
    });
  }, [agentConfig, agentName, isResuming, loadConversationHistory, localSessionId]);

  useEffect(() => {
    if (!resumeConversationData || resumeConversationData === processedResumeData.current) return;

    processedResumeData.current = resumeConversationData;

    if (resumeConversationData.success === false) {
      const errorMessage = {
        id: Date.now() + Math.random(),
        sender: 'system',
        content: resumeConversationData.technical_error || resumeConversationData.error || 'Failed to load conversation.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoadingInitialMessage(false);
      setIsResuming(false);
      focusInput();
      return;
    }

    const restoredMessages = (resumeConversationData.messages || []).map((message, index) => ({
      id: message.id || message.messageId || `${message.role}-${index}`,
      sender: message.role === 'assistant' ? 'agent' : message.role,
      content: message.content || '',
      timestamp: new Date(message.timestamp || Date.now()),
      agentName: message.role === 'assistant' ? agentName : undefined,
    }));

    setMessages(restoredMessages);
    setConversationId(resumeConversationData.conversationId || null);
    setHasStartedConversation(true);
    setIsLoadingInitialMessage(false);
    setIsResuming(false);
    setShowStudyBar(false);
    focusInput();
  }, [agentName, resumeConversationData]);

  useEffect(() => {
    if (!resumeConversationError) return;

    const errorMessage = {
      id: Date.now() + Math.random(),
      sender: 'system',
      content: resumeConversationError.message || 'Failed to load conversation.',
      timestamp: new Date(),
      isError: true
    };
    setMessages(prev => [...prev, errorMessage]);
    setIsLoadingInitialMessage(false);
    setIsResuming(false);
    focusInput();
  }, [resumeConversationError]);

  // Handle action response
  useEffect(() => {
    if (effectiveActionData && effectiveActionData !== processedActionData.current) {
      devLogData('Processing new actionData:', effectiveActionData);
      processedActionData.current = effectiveActionData;
      
      let isSuccess = false;
      let newConversationId = null;
      
      // Handle error cases first
      if (effectiveActionData.success === false || effectiveActionData.error || (effectiveActionData.errors && effectiveActionData.errors.length > 0)) {
        const errorMsg = buildActionErrorContent(
          effectiveActionData,
          effectiveActionData.errors?.[0]?.message || 'Unknown error'
        );
        const errorMessage = {
          id: Date.now() + Math.random(),
          sender: 'system',
          content: `Error: ${errorMsg}`,
          timestamp: new Date(),
          isError: true
        };
        devError('Agent chat V2 error:', errorMsg);
        setMessages(prev => [...prev, errorMessage]);
        setIsTyping(false);
        setIsLoadingInitialMessage(false);
        return;
      }
      
      // Handle V2 response format with success flag
      if (effectiveActionData.success === true) {
        isSuccess = true;
        
        // Extract conversationId if present
        if (effectiveActionData.conversationId) {
          newConversationId = effectiveActionData.conversationId;
        }
        
        // Check for new format with multiple messages
        if (effectiveActionData.messages && Array.isArray(effectiveActionData.messages)) {
          devLog('Processing multiple messages format');
          
          // Sort messages by timestamp to ensure chronological order
          const sortedMessages = [...effectiveActionData.messages].sort((a, b) => {
            const timeA = new Date(a.timestamp || Date.now());
            const timeB = new Date(b.timestamp || Date.now());
            return timeA - timeB;
          });
          
          // Process each message
          const newMessages = [];
          let messageIdOffset = 0;
          
          for (const msg of sortedMessages) {
            let sender = 'agent'; // default
            let content = '';
            
            // Determine sender based on role or message type
            if (msg.role === 'assistant' || msg.sender === 'agent') {
              sender = 'agent';
            } else if (msg.role === 'user' || msg.sender === 'user') {
              sender = 'user';
            } else if (msg.role === 'system' || msg.sender === 'system') {
              sender = 'system';
            }
            
            // Extract content
            if (typeof msg.content === 'string') {
              content = msg.content;
            } else if (typeof msg.content === 'object' && msg.content.text) {
              content = msg.content.text;
            } else if (msg.response) {
              content = msg.response;
            } else if (msg.message) {
              content = msg.message;
            } else {
              content = JSON.stringify(msg.content || msg);
            }
            
            // Format content to preserve line breaks
            const formattedContent = content.replace(/\n/g, '\n');
            
            const frontendMessage = {
              id: Date.now() + Math.random() + messageIdOffset++,
              sender: sender,
              content: formattedContent,
              timestamp: new Date(msg.timestamp || Date.now()),
              agentName: sender === 'agent' ? agentName : undefined,
              webSearchQueries: msg.webSearchQueries || msg.web_search_queries,
              citations: msg.citations,
              isSearching: msg.isSearching || msg.is_searching
            };
            
            newMessages.push(frontendMessage);
          }
          
          devLogData('Adding multiple messages:', newMessages);
          setMessages(prev => [...prev, ...newMessages]);
          setHasStartedConversation(true);
        }
        // Fall back to old single response format
        else {
          devLog('Processing single response format');
          let responseContent = 'I received your message but couldn\'t parse the response.';
          
          // Extract response content
          if (effectiveActionData.result) {
            if (typeof effectiveActionData.result === 'string') {
              responseContent = effectiveActionData.result;
            } else if (typeof effectiveActionData.result === 'object') {
              // Handle object result with conversationId
              if (effectiveActionData.result.conversationId && !newConversationId) {
                newConversationId = effectiveActionData.result.conversationId;
              }
              responseContent = effectiveActionData.result.response || effectiveActionData.result.content || JSON.stringify(effectiveActionData.result);
            }
          } else {
            responseContent = effectiveActionData.response || effectiveActionData.content || 'Success';
          }
          
          // Format response content to preserve line breaks
          const formattedContent = responseContent.replace(/\n/g, '\n');
          
          const agentMessage = {
            id: Date.now() + Math.random(),
            sender: 'agent',
            content: formattedContent,
            timestamp: new Date(),
            agentName: agentName,
            webSearchQueries: effectiveActionData.webSearchQueries || effectiveActionData.web_search_queries,
            citations: effectiveActionData.citations,
            isSearching: effectiveActionData.isSearching || effectiveActionData.is_searching
          };
          devLogData('Adding single agent message:', agentMessage);
          setMessages(prev => [...prev, agentMessage]);
          setHasStartedConversation(true);
        }
      } else {
        // Show parsing error for non-success responses
        const errorMessage = {
          id: Date.now() + Math.random(),
          sender: 'system',
          content: buildActionErrorContent(effectiveActionData, 'Failed to parse response. Please try again.'),
          timestamp: new Date(),
          isError: true
        };
        devLogData('Adding parsing error message:', errorMessage);
        setMessages(prev => [...prev, errorMessage]);
      }
      
      // Update conversationId if we got a new one
      if (newConversationId && newConversationId !== conversationId) {
        devLog('Updating conversationId from', conversationId, 'to', newConversationId);
        setConversationId(newConversationId);
      }
      
      setIsTyping(false);
      setIsLoadingInitialMessage(false);
      focusInput();

      if (effectiveActionData.backgroundJob?.jobId && !activeBackgroundJobsRef.current.has(effectiveActionData.backgroundJob.jobId)) {
        activeBackgroundJobsRef.current.add(effectiveActionData.backgroundJob.jobId);

        (async () => {
          try {
            while (isMountedRef.current) {
              const pollResult = await pollMaiaPracticeGeneration({
                jobId: effectiveActionData.backgroundJob.jobId,
              });

              if (!pollResult) {
                break;
              }

              if (pollResult.success && pollResult.status === 'completed') {
                const agentMessage = {
                  id: Date.now() + Math.random(),
                  sender: 'agent',
                  content: pollResult.response,
                  timestamp: new Date(),
                  agentName: agentName,
                };
                setMessages(prev => [...prev, agentMessage]);
                focusInput();
                break;
              }

              if (!pollResult.success || pollResult.status === 'failed' || pollResult.status === 'cancelled') {
                const errorMessage = {
                  id: Date.now() + Math.random(),
                  sender: 'system',
                  content: pollResult.message || pollResult.error || 'Background question generation failed.',
                  timestamp: new Date(),
                  isError: true
                };
                setMessages(prev => [...prev, errorMessage]);
                focusInput();
                break;
              }

              await new Promise((resolve) => setTimeout(resolve, 3000));
            }
          } catch (pollError) {
            const errorMessage = {
              id: Date.now() + Math.random(),
              sender: 'system',
              content: 'Background question generation was interrupted. Try asking again.',
              timestamp: new Date(),
              isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
            focusInput();
          } finally {
            activeBackgroundJobsRef.current.delete(effectiveActionData.backgroundJob.jobId);
          }
        })();
      }
    }
  }, [effectiveActionData, agentName, conversationId, pollMaiaPracticeGeneration]);

  // Handle send error
  useEffect(() => {
    if (effectiveSendError) {
      const errorMessage = {
        id: Date.now() + Math.random(),
        sender: 'system',
        content: effectiveSendError.message || 'Sorry, there was an error sending your message. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsTyping(false);
      setIsLoadingInitialMessage(false);
      setIsLoadingInitialMessage(false);
      focusInput();
    }
  }, [effectiveSendError]);

  const getAgentDisplayName = (name) => {
    const displayNames = {
      'don': 'Don - Medical Expert',
      'glenn': 'Glenn - Documentation Assistant',
      'gerald': 'Gerald - Skills Trainer',
      'maia': 'Maia - Study Companion'
    };
    return displayNames[name] || name;
  };

  const getAgentDescription = (agent) => {
    if (agent?.description) {
      return agent.description;
    }
    
    const defaultDescriptions = {
      'don': 'Advanced medical knowledge and clinical decision support',
      'glenn': 'Streamlined documentation and report generation',
      'gerald': 'Interactive skills training and assessment',
      'maia': 'Personalized study assistance and exam preparation'
    };
    
    return defaultDescriptions[agentName] || 'AI Assistant for medical education and training';
  };

  const handleNewConversation = async () => {
    processedResumeData.current = null;
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const newSessionId = `${agentName}_${timestamp}_${randomString}`;
    
    setLocalSessionId(newSessionId);
    setConversationId(null);
    setMessages([]);
    setIsResuming(false);
    setShowResumeModal(false);
    setHasStartedConversation(false);
    setIsLoadingInitialMessage(true);
    setShowStudyBar(isMaiaChat);
    setHasUsedStudyMode(false);

    try {
      let initiatorMessage = null;

      if (isMaiaChat) {
        initiatorMessage = "__maia_opening__";
      } else if (agentConfig?.initiatorMessage) {
        if (typeof agentConfig.initiatorMessage === 'string') {
          initiatorMessage = agentConfig.initiatorMessage.trim();
        } else if (typeof agentConfig.initiatorMessage === 'object' && agentConfig.initiatorMessage !== null) {
          if (agentConfig.initiatorMessage.markdown) {
            initiatorMessage = agentConfig.initiatorMessage.markdown.trim();
          } else if (agentConfig.initiatorMessage.content) {
            initiatorMessage = agentConfig.initiatorMessage.content.trim();
          } else if (agentConfig.initiatorMessage.text) {
            initiatorMessage = agentConfig.initiatorMessage.text.trim();
          }
        }
      }

      if (!initiatorMessage) {
        const agentFallbacks = {
          'don': 'Hello! I\'m Don, your medical expert assistant. How can I help you with medical questions today?',
          'glenn': 'Hi! I\'m Glenn, your documentation assistant. What documentation can I help you create or review?',
          'gerald': 'Welcome! I\'m Gerald, your skills trainer. What skills would you like to practice or learn about today?',
          'maia': '__maia_opening__',
          'maiav2': '__maia_opening__'
        };

        initiatorMessage = agentFallbacks[agentName.toLowerCase()] ||
                          agentFallbacks[agentConfig?.name?.toLowerCase()] ||
                          `Hello! I'm ${agentConfig?.name || agentName}, your AI assistant. How can I help you today?`;
      }

      await runChatAction({
        agentType: agentName,
        input: initiatorMessage,
        conversationId: null,
        localSessionId: newSessionId,
        model: isMaiaChat ? "gpt-5-mini" : "gpt-4o",
        maxTokens: 2000,
        temperature: 0.7,
        recentLimit: 6
      });
    } catch (error) {
      devError('Error starting new conversation:', error);
      setIsLoadingInitialMessage(false);
      const errorMessage = {
        id: Date.now() + Math.random(),
        sender: 'system',
        content: 'Failed to start conversation. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      focusInput();
    }
  };

  const handleResumeConversation = (resumeLocalSessionId) => {
    processedResumeData.current = null;
    if (resumeLocalSessionId) {
      setLocalSessionId(resumeLocalSessionId);
    }
    setMessages([]);
    setConversationId(null);
    setHasStartedConversation(false);
    setIsLoadingInitialMessage(true);
    setIsResuming(true);
    setShowResumeModal(false);
    setShowStudyBar(false);
    setHasUsedStudyMode(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || effectiveSendingMessage || !localSessionId || !agentConfig) return;

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    if (isMaiaChat && !hasUsedStudyMode) {
      setShowStudyBar(false);
    }

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      await runChatAction({
        agentType: agentName,
        input: inputMessage.trim(),
        conversationId: conversationId,
        localSessionId: localSessionId,
        model: isMaiaChat ? "gpt-5-mini" : "gpt-4o",
        maxTokens: 2000,
        temperature: 0.7,
        recentLimit: 6
      });

      focusInput();

      // Reset resuming flag after first message
      if (isResuming) {
        setIsResuming(false);
      }
    } catch (error) {
      devError('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + Math.random(),
        sender: 'system',
        content: 'Failed to send message. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsTyping(false);
      focusInput();
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const toggleSearchExpanded = (messageId) => {
    setExpandedSearches(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const formatCitation = (citation, format = 'APA') => {
    const { title, url, domain, author, date } = citation;
    const currentDate = new Date().toLocaleDateString();
    
    switch(format) {
      case 'APA':
        return `${domain || 'Website'}. (${date || currentDate}). ${title}. Retrieved from ${url}`;
      case 'MLA':
        return `"${title}." ${domain || 'Web'}. ${date || currentDate}. <${url}>.`;
      case 'Chicago':
        return `"${title}." ${domain || 'Website'}. Accessed ${currentDate}. ${url}.`;
      case 'BibTeX':
        return `@misc{${domain?.replace(/\./g, '')},\n  title={${title}},\n  url={${url}},\n  note={Accessed: ${currentDate}}\n}`;
      default:
        return `${title} - ${url}`;
    }
  };

  const exportCitations = (citations, format = 'APA') => {
    const formatted = citations.map((citation, idx) => 
      `[${idx + 1}] ${formatCitation(citation, format)}`
    ).join('\n\n');
    
    const blob = new Blob([formatted], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `citations-${format.toLowerCase()}-${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const copyAllCitations = (citations, format = 'APA') => {
    const formatted = citations.map((citation, idx) => 
      `[${idx + 1}] ${formatCitation(citation, format)}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(formatted);
  };

  const handleQuickAction = (prompt) => {
    setHasUsedStudyMode(true);
    setShowStudyBar(false);
    setInputMessage(prompt);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        resizeInput(inputRef.current);
      }
    }, 0);
  };

  // Loading state
  if (loadingAgent) {
    return (
      <div className="agent-chat-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading agent...</p>
        </div>
      </div>
    );
  }

  // Agent not found
  if (agentError || !agentConfig) {
    return (
      <div className="agent-chat-page">
        <div className="error-container">
          <h2>Agent Not Found</h2>
          <p>The agent "{agentName}" could not be found or is not currently active.</p>
          <div className="debug-info">
            <p><strong>URL parameter:</strong> {agentName}</p>
            <p><strong>Searching for name:</strong> {agentName}</p>
            {agentError && <p><strong>Error:</strong> {agentError.message}</p>}
          </div>
          <Link to="/agents" className="back-link">← Back to Agents</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-chat-page">
      {/* Resume Conversation Modal */}
      {showResumeModal && (
        <ResumeConversationModal
          isOpen={showResumeModal}
          onClose={() => setShowResumeModal(false)}
          onNewConversation={handleNewConversation}
          onResumeConversation={handleResumeConversation}
          agentType={agentName}
          agentName={agentConfig?.name || agentName}
        />
      )}

      {/* Header */}
      <div className="chat-header">
        <Link to="/" className="back-button">←</Link>
        <h1 className="agent-name">{agentConfig.name}</h1>
        <button
          type="button"
          className="conversation-list-button"
          onClick={() => setShowResumeModal(true)}
        >
          Conversations
        </button>
      </div>

      {/* Messages Area */}
      <div className="messages-container">
        <div className="messages-area">
          {messages.length === 0 && !isLoadingInitialMessage && (
            <div className="welcome-message">
              <div className="welcome-card">
                <h3>Welcome to {getAgentDisplayName(agentConfig.name)}</h3>
                {isMaiaChat ? (
                  <p>Tell Maia what you want to work on, or use one of the prompts below.</p>
                ) : (
                  <p>{getAgentDescription(agentConfig)}</p>
                )}
              </div>
            </div>
          )}

          {isLoadingInitialMessage && (
            <div className="welcome-message">
              <div className="welcome-card">
                <h3>Starting conversation with {getAgentDisplayName(agentConfig.name)}</h3>
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Getting ready to chat...</p>
                </div>
              </div>
            </div>
          )}
          
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender} ${message.isError ? 'error' : ''}`}
            >
              <div className="message-content">
                <div className="message-text">{message.content}</div>
                
                {/* Web Search Activity Section */}
                {message.webSearchQueries && message.webSearchQueries.length > 0 && (
                  <div className="web-search-section">
                    <div 
                      className="web-search-header"
                      onClick={() => toggleSearchExpanded(message.id)}
                    >
                      <FiSearch className="search-icon" />
                      <span className="search-label">
                        Web Search ({message.webSearchQueries.length} {message.webSearchQueries.length === 1 ? 'query' : 'queries'})
                      </span>
                      {expandedSearches[message.id] ? <FiChevronUp /> : <FiChevronDown />}
                    </div>
                    {expandedSearches[message.id] && (
                      <div className="web-search-queries">
                        {message.webSearchQueries.map((query, idx) => (
                          <div key={idx} className="search-query">
                            <span className="query-number">{idx + 1}.</span>
                            <span className="query-text">{query}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Citations Section */}
                {message.citations && message.citations.length > 0 && (
                  <div className="citations-section">
                    <div className="citations-toolbar">
                      <div className="citations-header">Sources:</div>
                      <div className="citations-actions">
                        <select 
                          value={citationExportFormat}
                          onChange={(e) => setCitationExportFormat(e.target.value)}
                          className="citation-format-select"
                        >
                          <option value="APA">APA</option>
                          <option value="MLA">MLA</option>
                          <option value="Chicago">Chicago</option>
                          <option value="BibTeX">BibTeX</option>
                        </select>
                        <button 
                          onClick={() => copyAllCitations(message.citations, citationExportFormat)}
                          className="citation-action-btn"
                          title="Copy all citations"
                        >
                          <FiCopy />
                        </button>
                        <button 
                          onClick={() => exportCitations(message.citations, citationExportFormat)}
                          className="citation-action-btn"
                          title="Export citations"
                        >
                          <FiDownload />
                        </button>
                      </div>
                    </div>
                    <div className="citations-list">
                      {message.citations.map((citation, idx) => (
                        <div key={idx} className="citation-item">
                          <span className="citation-number">[{citation.number || idx + 1}]</span>
                          <div className="citation-details">
                            <a 
                              href={citation.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="citation-link"
                            >
                              <span className="citation-title">{citation.title}</span>
                              <FiExternalLink className="external-icon" />
                            </a>
                            {citation.domain && (
                              <span className="citation-domain">{citation.domain}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="message-timestamp">
                  {formatTimestamp(message.timestamp)}
                </div>
              </div>
            </div>
          ))}
          
          {(isTyping || effectiveSendingMessage || loadingResumedConversation) && !isLoadingInitialMessage && (
            <div className="message agent typing">
              <div className="message-content">
                {/* Check if the last user message or any pending action suggests web search */}
                {messages.length > 0 && messages[messages.length - 1].isSearching ? (
                  <div className="search-indicator">
                    <FiSearch className="search-icon-loading" />
                    <span>Searching the web...</span>
                  </div>
                ) : (
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}
                <div className="message-timestamp">
                  {messages.length > 0 && messages[messages.length - 1].isSearching 
                    ? 'Finding relevant information...' 
                    : `${agentConfig.name} is thinking...`}
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="input-container">
        {isMaiaChat && (
          <>
            {showStudyBar ? (
              <div className="maia-study-bar">
                <div className="maia-study-copy">
                  <span className="maia-study-title">Study Modes</span>
                  <span className="maia-study-subtitle">Persistent context plus grounded clinical knowledge</span>
                </div>
                <div className="maia-quick-actions">
                  {maiaQuickActions.map((action) => (
                    <button
                      key={action.label}
                      type="button"
                      className="maia-quick-action"
                      onClick={() => handleQuickAction(action.prompt)}
                      disabled={effectiveSendingMessage || isLoadingInitialMessage}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="maia-study-toggle-row">
                <button
                  type="button"
                  className="maia-study-toggle"
                  onClick={() => setShowStudyBar(true)}
                  disabled={effectiveSendingMessage || isLoadingInitialMessage}
                >
                  Open Study Modes
                </button>
              </div>
            )}
          </>
        )}
        {(isTyping || effectiveSendingMessage || loadingResumedConversation) && !isLoadingInitialMessage && (
          <div className="response-status" aria-live="polite">
            <span className="response-status-label">
              {loadingResumedConversation ? "Loading conversation" : `${agentConfig.name} is working`}
            </span>
            <span className="response-status-dots" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </div>
        )}
        <form onSubmit={handleSendMessage} className="message-form">
          <div className="input-group">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                resizeInput(e.target);
              }}
              placeholder={`Type a message to ${agentConfig.name}...`}
              disabled={effectiveSendingMessage}
              className="message-input"
              rows={4}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || effectiveSendingMessage || isLoadingInitialMessage}
              className="send-button"
            >
              {effectiveSendingMessage || isLoadingInitialMessage ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .agent-chat-page {
          display: flex;
          flex-direction: column;
          height: 100vh;
          height: 100dvh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          color: #FFFFFF;
          overflow: hidden;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }

        .loading-container, .error-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          height: 100vh;
          text-align: center;
          background: rgba(26, 26, 46, 0.95);
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #2d2d2d;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-container h2 {
          color: #ffffff;
          margin-bottom: 1rem;
          font-weight: 700;
        }

        .error-container p {
          color: #b0b0b0;
          margin-bottom: 1rem;
        }

        .debug-info {
          background: #1e1e1e;
          padding: 1.5rem;
          border-radius: 12px;
          margin: 1rem 0;
          border: 1px solid #2d2d2d;
          text-align: left;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .debug-info p {
          margin: 0.5rem 0;
          font-family: 'Fira Code', monospace;
          font-size: 0.85rem;
          color: #b0b0b0;
        }

        .debug-info strong {
          color: #ffffff;
        }

        .back-link {
          color: #3b82f6;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .back-link:hover {
          color: #60a5fa;
          transform: translateX(-2px);
        }

        .chat-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 50px;
          display: flex;
          align-items: center;
          padding: 0 16px;
          background: rgba(255, 255, 255, 0.1);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(20px);
          z-index: 100;
          flex-shrink: 0;
        }

        .back-button {
          color: #007AFF;
          text-decoration: none;
          font-weight: 600;
          font-size: 1.25rem;
          padding: 8px;
          margin-right: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 36px;
        }

        .back-button:hover {
          opacity: 0.7;
        }

        .agent-name {
          flex: 1;
          text-align: center;
          font-size: 1rem;
          font-weight: 600;
          color: #FFFFFF;
          margin: 0;
        }

        .conversation-list-button {
          border: 1px solid rgba(59, 130, 246, 0.45);
          background: rgba(59, 130, 246, 0.15);
          color: #bfdbfe;
          border-radius: 999px;
          padding: 0.35rem 0.7rem;
          font-size: 0.8rem;
          font-weight: 600;
          line-height: 1;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .conversation-list-button:hover {
          background: rgba(59, 130, 246, 0.25);
          color: #ffffff;
        }

        .header-spacer {
          width: 36px;
          min-width: 36px;
        }

        .messages-container {
          flex: 1;
          overflow: hidden;
          background: transparent;
          position: relative;
          min-height: 0;
          display: flex;
          flex-direction: column;
          padding-top: 50px;
          padding-bottom: 150px;
        }

        .messages-area {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        .messages-area::-webkit-scrollbar {
          display: none;
        }

        .welcome-message {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
        }

        .welcome-card {
          background: rgba(255, 255, 255, 0.1);
          padding: 1.5rem;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(20px);
          text-align: center;
          max-width: 400px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .welcome-card h3 {
          color: #FFFFFF;
          margin-bottom: 0.75rem;
          font-weight: 600;
          font-size: 1.125rem;
        }

        .welcome-card p {
          color: #E5E5E5;
          margin-bottom: 0.5rem;
          line-height: 1.5;
          font-size: 0.9rem;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          margin-top: 1rem;
        }

        .loading-spinner .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e0e0e0;
          border-top: 3px solid #007AFF;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-spinner p {
          margin: 0;
          font-size: 0.9rem;
          color: #E5E5E5;
        }

        .message {
          display: flex;
          margin-bottom: 6px;
          animation: messageSlideIn 0.2s ease-out;
        }

        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .message.user {
          justify-content: flex-end;
        }

        .message.agent {
          justify-content: flex-start;
        }

        .message.system {
          justify-content: center;
        }

        .message-content {
          max-width: 75%;
          padding: 10px 14px;
          border-radius: 18px;
          position: relative;
        }

        .message.user .message-content {
          background: #007AFF;
          color: #ffffff;
          border-bottom-right-radius: 4px;
        }

        .message.agent .message-content {
          background: rgba(255, 255, 255, 0.15);
          color: #FFFFFF;
          backdrop-filter: blur(10px);
          border-bottom-left-radius: 4px;
        }

        .message.system .message-content {
          background: #FF3B30;
          color: #ffffff;
          max-width: 85%;
          border-radius: 12px;
        }

        .message.error .message-content {
          background: #FF3B30;
          color: #ffffff;
          border-radius: 12px;
        }

        .message-text {
          word-wrap: break-word;
          line-height: 1.35;
          font-size: 0.9375rem;
          white-space: pre-wrap;
        }

        .message-timestamp {
          font-size: 0.625rem;
          opacity: 0.5;
          margin-top: 4px;
          color: inherit;
          font-weight: 400;
          text-align: right;
        }

        .message.agent .message-timestamp {
          text-align: left;
        }

        .web-search-section {
          margin-top: 8px;
          padding: 8px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 8px;
        }

        .web-search-header {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          font-size: 0.8125rem;
          color: #60a5fa;
          user-select: none;
        }

        .web-search-header:hover {
          color: #93c5fd;
        }

        .search-icon {
          font-size: 0.875rem;
        }

        .search-label {
          flex: 1;
          font-weight: 500;
        }

        .web-search-queries {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid rgba(59, 130, 246, 0.2);
        }

        .search-query {
          display: flex;
          gap: 6px;
          padding: 4px 0;
          font-size: 0.75rem;
          color: #e0e0e0;
        }

        .query-number {
          color: #60a5fa;
          font-weight: 600;
          min-width: 20px;
        }

        .query-text {
          flex: 1;
          line-height: 1.4;
        }

        .citations-section {
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .citations-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
          gap: 8px;
        }

        .citations-header {
          font-size: 0.75rem;
          font-weight: 600;
          color: #b0b0b0;
          flex-shrink: 0;
        }

        .citations-actions {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .citation-format-select {
          font-size: 0.6875rem;
          padding: 3px 6px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 4px;
          background: rgba(255, 255, 255, 0.1);
          color: #e0e0e0;
          cursor: pointer;
          outline: none;
          transition: all 0.2s ease;
        }

        .citation-format-select:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
        }

        .citation-format-select:focus {
          border-color: #60a5fa;
        }

        .citation-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 4px;
          color: #60a5fa;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.75rem;
        }

        .citation-action-btn:hover {
          background: rgba(59, 130, 246, 0.3);
          border-color: rgba(59, 130, 246, 0.5);
          color: #93c5fd;
        }

        .citation-action-btn:active {
          transform: scale(0.95);
        }

        .citations-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .citation-item {
          display: flex;
          gap: 6px;
          font-size: 0.75rem;
          align-items: flex-start;
        }

        .citation-number {
          color: #60a5fa;
          font-weight: 600;
          min-width: 24px;
          flex-shrink: 0;
        }

        .citation-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .citation-link {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #93c5fd;
          text-decoration: none;
          transition: color 0.2s ease;
          word-break: break-word;
        }

        .citation-link:hover {
          color: #bfdbfe;
          text-decoration: underline;
        }

        .citation-title {
          flex: 1;
          line-height: 1.3;
        }

        .external-icon {
          font-size: 0.625rem;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .citation-domain {
          color: #8e8e93;
          font-size: 0.6875rem;
          font-style: italic;
        }

        .search-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #60a5fa;
        }

        .search-icon-loading {
          font-size: 1rem;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { 
            opacity: 1;
            transform: scale(1);
          }
          50% { 
            opacity: 0.5;
            transform: scale(1.1);
          }
        }

        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .typing-indicator span {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #8E8E93;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
        .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

        @keyframes typing {
          0%, 80%, 100% { 
            transform: scale(0.7);
            opacity: 0.4;
          }
          40% { 
            transform: scale(1);
            opacity: 1;
          }
        }

        .input-container {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 8px 12px 12px;
          background: linear-gradient(180deg, rgba(15, 52, 96, 0) 0%, rgba(9, 20, 42, 0.92) 28%, rgba(9, 20, 42, 0.98) 100%);
          border-top: 1px solid rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(18px);
          flex-shrink: 0;
          z-index: 100;
        }

        .maia-study-bar {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 10px 12px;
          border: 1px solid rgba(147, 197, 253, 0.16);
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.08);
        }

        .maia-study-toggle-row {
          display: flex;
          justify-content: flex-start;
        }

        .maia-study-toggle {
          border: 1px solid rgba(147, 197, 253, 0.24);
          background: rgba(15, 23, 42, 0.7);
          color: #bfdbfe;
          border-radius: 999px;
          padding: 6px 12px;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .maia-study-toggle:hover:enabled {
          background: rgba(30, 64, 175, 0.24);
          border-color: rgba(147, 197, 253, 0.42);
        }

        .maia-study-toggle:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .maia-study-copy {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .maia-study-title {
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #93c5fd;
        }

        .maia-study-subtitle {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.8);
        }

        .maia-quick-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .maia-quick-action {
          border: 1px solid rgba(147, 197, 253, 0.28);
          background: rgba(30, 41, 59, 0.72);
          color: #e2e8f0;
          border-radius: 999px;
          padding: 7px 12px;
          font-size: 0.78rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .maia-quick-action:hover:enabled {
          transform: translateY(-1px);
          background: rgba(59, 130, 246, 0.2);
          border-color: rgba(147, 197, 253, 0.44);
        }

        .maia-quick-action:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .message-form {
          max-width: 100%;
          margin: 0;
        }

        .response-status {
          display: inline-flex;
          align-items: center;
          align-self: flex-start;
          gap: 8px;
          padding: 6px 10px;
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.66);
          border: 1px solid rgba(147, 197, 253, 0.18);
          color: rgba(255, 255, 255, 0.88);
          font-size: 0.76rem;
          font-weight: 600;
        }

        .response-status-label {
          white-space: nowrap;
        }

        .response-status-dots {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .response-status-dots span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #93c5fd;
          animation: thinkingPulse 1.1s infinite ease-in-out;
        }

        .response-status-dots span:nth-child(2) {
          animation-delay: 0.15s;
        }

        .response-status-dots span:nth-child(3) {
          animation-delay: 0.3s;
        }

        .input-group {
          display: flex;
          gap: 8px;
          align-items: flex-end;
        }

        .message-input {
          flex: 1;
          padding: 8px 14px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 20px;
          font-size: 16px;
          outline: none;
          transition: all 0.15s ease;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          color: #FFFFFF;
          min-height: 72px;
          max-height: 120px;
          resize: none;
          overflow-y: hidden;
          font-family: inherit;
          line-height: 1.4;
        }

        .message-input:focus {
          border-color: #007AFF;
          box-shadow: 0 0 0 1px #007AFF;
          background: rgba(255, 255, 255, 0.15);
        }

        .message-input:disabled {
          background: rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.5);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .message-input::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }

        @keyframes thinkingPulse {
          0%, 80%, 100% {
            opacity: 0.35;
            transform: translateY(0);
          }
          40% {
            opacity: 1;
            transform: translateY(-2px);
          }
        }

        .send-button {
          width: 36px;
          height: 36px;
          padding: 0;
          background: #007AFF;
          color: #ffffff;
          border: none;
          border-radius: 50%;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .send-button:hover:not(:disabled) {
          background: #0051D5;
          transform: scale(1.05);
        }

        .send-button:active:not(:disabled) {
          transform: scale(0.95);
        }

        .send-button:disabled {
          background: rgba(255, 255, 255, 0.2);
          cursor: not-allowed;
          color: rgba(255, 255, 255, 0.5);
          transform: none;
        }

        @media (max-width: 768px) {
          .agent-chat-page {
            height: 100vh;
            height: 100dvh;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            overflow: hidden;
          }

          .chat-header {
            padding: 0.5rem 1rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
            min-height: auto;
            flex-shrink: 0;
          }

          .chat-header::before {
            height: 1px;
          }

          .header-nav {
            margin-bottom: 0.25rem;
          }

          .back-button {
            font-size: 0.8rem;
          }

          .conversation-list-button {
            font-size: 0.72rem;
            padding: 0.3rem 0.55rem;
          }

          .agent-info h1 {
            font-size: 1rem;
            margin-bottom: 0.125rem;
            line-height: 1.2;
          }

          .agent-description {
            font-size: 0.75rem;
            line-height: 1.2;
            opacity: 0.8;
            max-lines: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .messages-area {
            padding: 0.75rem;
            gap: 0.5rem;
            flex: 1;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }

          .message {
            margin-bottom: 0.25rem;
          }

          .message-content {
            max-width: 85%;
            padding: 0.5rem 0.75rem;
            border-radius: 16px;
          }

          .message-text {
            font-size: 0.875rem;
            line-height: 1.3;
          }

          .message-timestamp {
            font-size: 0.6rem;
            margin-top: 0.1875rem;
          }

          .web-search-section {
            margin-top: 6px;
            padding: 6px;
          }

          .web-search-header {
            font-size: 0.75rem;
            gap: 4px;
          }

          .search-icon {
            font-size: 0.8125rem;
          }

          .search-query {
            font-size: 0.6875rem;
            padding: 3px 0;
          }

          .citations-section {
            margin-top: 10px;
            padding-top: 6px;
          }

          .citations-toolbar {
            margin-bottom: 6px;
            gap: 6px;
          }

          .citations-header {
            font-size: 0.6875rem;
          }

          .citations-actions {
            gap: 4px;
          }

          .citation-format-select {
            font-size: 0.625rem;
            padding: 2px 4px;
          }

          .citation-action-btn {
            padding: 3px;
            font-size: 0.6875rem;
          }

          .citation-item {
            font-size: 0.6875rem;
            gap: 4px;
          }

          .citation-number {
            min-width: 20px;
          }

          .external-icon {
            font-size: 0.5625rem;
          }

          .citation-domain {
            font-size: 0.625rem;
          }

          .search-indicator {
            gap: 6px;
            font-size: 0.875rem;
          }

          .search-icon-loading {
            font-size: 0.9rem;
          }

          .input-container {
            padding: 0.75rem 1rem;
            padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
          }

          .maia-study-bar {
            padding: 8px 10px;
          }

          .maia-study-toggle {
            padding: 5px 10px;
            font-size: 0.68rem;
          }

          .maia-study-subtitle {
            font-size: 0.7rem;
          }

          .maia-quick-actions {
            gap: 6px;
          }

          .maia-quick-action {
            padding: 6px 10px;
            font-size: 0.72rem;
          }

          .input-group {
            gap: 0.5rem;
            align-items: flex-end;
          }

          .response-status {
            padding: 5px 9px;
            font-size: 0.7rem;
            gap: 6px;
          }

          .message-input {
            min-height: 2.25rem;
            max-height: 6rem;
            padding: 0.625rem 0.875rem;
            border-radius: 20px;
            font-size: 0.9rem;
            border: 1px solid rgba(255, 255, 255, 0.3);
          }

          .message-input:focus {
            border-color: #007AFF;
            box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.3);
          }

          .send-button {
            padding: 0.625rem 1rem;
            border-radius: 20px;
            font-size: 0.9rem;
            min-width: 60px;
            height: 2.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .welcome-card {
            padding: 1.5rem;
            margin: 0.75rem;
            border-radius: 12px;
          }

          .welcome-card h3 {
            font-size: 1.25rem;
            margin-bottom: 0.75rem;
          }

          .welcome-card p {
            font-size: 0.9rem;
            margin-bottom: 0.5rem;
          }

          .typing-indicator span {
            width: 6px;
            height: 6px;
          }

          .web-search-section {
            padding: 5px;
            margin-top: 5px;
          }

          .web-search-header {
            font-size: 0.7rem;
          }

          .search-query {
            font-size: 0.65rem;
          }

          .citations-section {
            margin-top: 8px;
            padding-top: 5px;
          }

          .citations-toolbar {
            flex-wrap: wrap;
            margin-bottom: 4px;
            gap: 4px;
          }

          .citations-header {
            font-size: 0.65rem;
          }

          .citation-format-select {
            font-size: 0.6rem;
            padding: 2px 3px;
          }

          .citation-action-btn {
            padding: 2px;
            font-size: 0.65rem;
          }

          .citation-item {
            font-size: 0.65rem;
          }

          .search-indicator {
            font-size: 0.8125rem;
          }

          /* Better mobile scrolling */
          .messages-area::-webkit-scrollbar {
            display: none;
          }

          /* Fix for mobile keyboard */
          .message-input:focus {
            transform: none;
          }
        }

        /* Extra small mobile devices */
        @media (max-width: 480px) {
          .chat-header {
            padding: 0.375rem 0.75rem;
          }

          .header-nav {
            margin-bottom: 0.125rem;
          }

          .back-button {
            font-size: 0.75rem;
          }

          .conversation-list-button {
            font-size: 0.68rem;
            padding: 0.28rem 0.5rem;
          }

          .agent-info h1 {
            font-size: 0.9rem;
            margin-bottom: 0;
            line-height: 1.1;
          }

          .agent-description {
            display: none; /* Hide description completely on very small screens */
          }

          .messages-area {
            padding: 0.5rem;
            gap: 0.375rem;
            flex: 1;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }

          .message {
            margin-bottom: 0.1875rem;
          }

          .message-content {
            max-width: 90%;
            padding: 0.4375rem 0.625rem;
            border-radius: 14px;
          }

          .message-text {
            font-size: 0.8125rem;
            line-height: 1.25;
          }

          .message-timestamp {
            font-size: 0.5625rem;
            margin-top: 0.125rem;
          }

          .input-container {
            padding: 0.5rem 0.75rem;
            padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0px));
          }

          .maia-study-bar {
            padding: 7px 8px;
            gap: 6px;
          }

          .maia-study-title {
            font-size: 0.68rem;
          }

          .maia-study-subtitle {
            font-size: 0.66rem;
          }

          .maia-study-toggle {
            padding: 5px 9px;
            font-size: 0.64rem;
          }

          .maia-quick-action {
            padding: 5px 9px;
            font-size: 0.68rem;
          }

          .response-status {
            font-size: 0.66rem;
            padding: 5px 8px;
          }

          .message-input {
            min-height: 3.75rem;
            padding: 0.5rem 0.75rem;
            font-size: 0.85rem;
            border-radius: 18px;
          }

          .send-button {
            padding: 0.5rem 0.875rem;
            height: 2rem;
            border-radius: 18px;
            font-size: 0.85rem;
            min-width: 55px;
          }

          .welcome-card {
            padding: 1.25rem;
            margin: 0.5rem;
          }
        }

        /* Landscape mobile optimization */
        @media (max-width: 768px) and (orientation: landscape) {
          .chat-header {
            padding: 0.25rem 1rem;
          }

          .header-nav {
            margin-bottom: 0.125rem;
          }

          .agent-info h1 {
            font-size: 0.875rem;
            margin-bottom: 0;
            line-height: 1.1;
          }

          .agent-description {
            display: none; /* Hide in landscape to save space */
          }

          .messages-area {
            padding: 0.5rem 0.75rem;
            flex: 1;
            overflow-y: auto;
          }

          .input-container {
            padding: 0.375rem 1rem;
          }
        }

        /* Safe area handling for newer mobile devices */
        @supports (padding: max(0px)) {
          .input-container {
            padding-bottom: max(1rem, env(safe-area-inset-bottom));
          }

          @media (max-width: 768px) {
            .input-container {
              padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
            }
          }

          @media (max-width: 480px) {
            .input-container {
              padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
            }
          }
        }
      `}</style>
    </div>
  );
};

export default AgentChat;
